import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import { fetchForecastAccess, setForecastAccess } from '@src/api/actions/dashboard/forecast';

const pct0 = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);
const monthShort = (iso) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
    : '';

function eligibilityNote(rec) {
  if (!rec) return '';
  const bar = rec.bar || {};
  if (!rec.graded_months || rec.leads_mape == null) return 'Not graded yet.';
  if ((rec.graded_months || 0) < (bar.min_months || 0)) {
    return `Only ${rec.graded_months} graded month${rec.graded_months === 1 ? '' : 's'} so far (needs ${bar.min_months}) — turning it on anyway is allowed.`;
  }
  if (!rec.eligible) return `Above the ${pct0(bar.max_leads_error)} bar — turning it on anyway is allowed.`;
  return `Within the ${pct0(bar.max_leads_error)} bar.`;
}

/**
 * Staff-only card: is this client's forecast switched on for their own users,
 * and how accurate has it been? Plus a toggle that previews the client view.
 */
export default function ForecastAccessPanel({ clientId, preview, onPreviewChange }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchForecastAccess({ client_id: clientId })
      .then((resp) => !cancelled && setRec(resp?.data || resp))
      .catch(() => !cancelled && setError('Could not load client access.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleToggle = (_e, enabled) => {
    setSaving(true);
    setError('');
    setForecastAccess({ client_id: Number(clientId), enabled, note: '' })
      .then((resp) => setRec(resp?.data || resp))
      .catch(() => setError('Could not change client access.'))
      .finally(() => setSaving(false));
  };

  const bar = rec?.bar || {};
  const months = rec?.months || [];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1, md: 3 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                checked={Boolean(rec?.enabled)}
                onChange={handleToggle}
                disabled={loading || saving || !rec}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Client access: {rec?.enabled ? 'On' : 'Off'}
              </Typography>
            }
          />
          {(loading || saving) && <CircularProgress size={14} />}
        </Stack>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          {rec && (
            <>
              <Typography variant="body2">
                Lead forecast error {pct0(rec.leads_mape)} over {rec.graded_months || 0} graded month
                {rec.graded_months === 1 ? '' : 's'} (bar {pct0(bar.max_leads_error)}, min {bar.min_months ?? '—'}
                {' '}months)
                {rec.band_coverage != null ? ` · in range ${pct0(rec.band_coverage)}` : ''}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: rec.eligible ? 'success.main' : 'warning.main' }}
              >
                {eligibilityNote(rec)}
              </Typography>
              {months.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {months
                    .map(
                      (m) =>
                        `${monthShort(m.period)}: ${Math.round(m.predicted_leads)} predicted vs ${m.actual_leads} actual (${pct0(m.leads_abs_pct_err)} off)`
                    )
                    .join(' · ')}
                </Typography>
              )}
              {rec.changed_at && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Last changed {new Date(rec.changed_at).toLocaleDateString()}
                  {rec.changed_by ? ` by ${rec.changed_by}` : ''}
                </Typography>
              )}
            </>
          )}
        </Box>

        <FormControlLabel
          sx={{ mr: 0 }}
          control={<Switch checked={Boolean(preview)} onChange={(_e, v) => onPreviewChange(v)} />}
          label={<Typography variant="body2">Preview client view</Typography>}
        />
      </Stack>
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
    </Paper>
  );
}
